// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Select, { SelectPresentationProps } from "@eisinc/MobiusOverrides/Select/Select";
import TextField, { TextFieldProps } from "@eisinc/MobiusOverrides/TextField/TextField";
import { SHOW_ALL_BRANDS_KEY } from "@eisinc/XDL/constants";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
    // XNG-Changes
    handleSearchFilterBrands?: (e: string) => void;
    searchFilterBrands?: string;
    lastLetterClicked?: string;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationLinkText?: TypographyPresentationProps;
    navigationTextEmpty?: TypographyPresentationProps;
    // XNG-Changes
    alphabetContainer?: InjectableCss;
    titleText?: TypographyPresentationProps;
    brandsLettersWrapper?: InjectableCss;
    alphabetSection?: InjectableCss;
    filterSection?: InjectableCss;
    filterInput?: TextFieldProps;
    selectWrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            text-transform: uppercase;
            justify-items: center;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
        `,
    },
    navigationItem: {
        css: css`
            margin: 0;
            display: inline-block;
            flex: 0 0 30px;
        `,
    },
    navigationLink: {
        css: css`
            display: block;
        `,
        typographyProps: {
            transform: "uppercase",
            variant: "headerTertiary",
            forwardAs: "span",
        },
    },
    navigationTextEmpty: {
        transform: "uppercase",
        variant: "headerTertiary",
        forwardAs: "span",
        color: "text.disabled",
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));
    // XNG-Changes
    const [alphabet] = useState(() => [{ letter: "all", count: 0, linkable: true }, ...props.alphabet]);

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };

    const createHandleClickOfLetter = (letter: string) => () => {
        // XNG-Change
        // const letterElement = document.getElementById(`${props.prefix || ""}letter-${letter}`);
        // if (letterElement) {
        //     const letterOffset = letterElement.offsetTop;
        //     if (letterOffset > 0) {
        //         window.scrollTo(0, letterOffset);
        //     }
        // }
        return handleClickOfLetter(letter);
    };

    if (alphabet.length <= 0) {
        return null;
    }

    // XNG-Changes : change structure
    return (
        <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation" id="alphabetNavigation">
            <StyledWrapper {...styles.brandsLettersWrapper}>
                <StyledWrapper {...styles.filterSection}>
                    <TextField
                        name="filterBrands"
                        value={props.searchFilterBrands}
                        placeholder={translate("Search by Name")}
                        onChange={e => props.handleSearchFilterBrands?.(e.target.value)}
                        label={translate("Search Brands")}
                        {...styles.filterInput}
                        iconClickableProps={{
                            onClick: () => props.handleSearchFilterBrands?.(props.searchFilterBrands || ""),
                            "aria-label": translate("Search by Name"),
                        }}
                        {...(props.searchFilterBrands
                            ? {
                                  clearClickableProps: {
                                      clickableProps: { onClick: () => props.handleSearchFilterBrands?.("") },
                                  },
                              }
                            : {})}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.alphabetSection}>
                    <Typography {...styles.titleText}>{translate("Filter Alphabetically")}</Typography>
                    <StyledWrapper {...styles.alphabetContainer}>
                        {alphabet.map(alphabetItem => (
                            <StyledSpan
                                {...styles.navigationItem}
                                key={alphabetItem.letter}
                                data-test-selector="alphabetNavigationItemLetter"
                            >
                                {alphabetItem.linkable ? (
                                    <Link
                                        {...styles.navigationLink}
                                        onClick={createHandleClickOfLetter(alphabetItem.letter)}
                                        aria-label={
                                            alphabetItem.letter === "#" ? translate("0-9") : alphabetItem.letter
                                        }
                                    >
                                        {alphabetItem.letter === "#" ? translate("0-9") : alphabetItem.letter}
                                    </Link>
                                ) : (
                                    <Typography {...styles.navigationTextEmpty}>{alphabetItem.letter}</Typography>
                                )}
                            </StyledSpan>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.selectWrapper}>
                    <Select
                        {...styles.select}
                        placeholder={translate("Search by First Letter")}
                        onChange={e => handleClickOfLetter(e.target.value)}
                        value={props.lastLetterClicked}
                        label={translate("Filter Alphabetically")}
                    >
                        {alphabet.map(alphabetItem => (
                            <option
                                disabled={!alphabetItem.linkable}
                                key={alphabetItem.letter}
                                value={alphabetItem.letter}
                            >
                                {alphabetItem.letter === "#"
                                    ? translate("0-9")
                                    : alphabetItem.letter === SHOW_ALL_BRANDS_KEY
                                      ? translate("Show All Brands")
                                      : alphabetItem.letter.toUpperCase()}
                            </option>
                        ))}
                    </Select>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
